import { Model } from "../types";

export interface Segment<T> {
  label: string;
  data: T[];
}

export type ListKeys = {
  id: string;
  label: string;
  group: string;
  active?: boolean;
};

// this function assumes that data is already sorted into the appropriate chronological (or other) order,
//  both in terms of the ordering of segments and the ordering within segments
export const segmentList = <T>(
  data: T[],
  getSegmentLabelFromItem: (item: T) => string
): Segment<T>[] => {
  const segments: Segment<T>[] = [];

  for (const item of data) {
    const label = getSegmentLabelFromItem(item);

    if (!segments.length || segments[segments.length - 1].label !== label) {
      // if this is the first record, or the label is different to it's predecessor, it is a new segment
      const thisSegment: Segment<T> = { label, data: [] };
      segments.push(thisSegment);
    }

    segments[segments.length - 1].data.push(item);
  }

  return segments;
};

type InjectedHeadersReturnType<T> = T | { label: string; isHeading: true };

export const injectHeaders = <T>(
  data: T[],
  getHeaderLabelFromItem: (item: T) => string
): InjectedHeadersReturnType<T>[] => {
  const results: InjectedHeadersReturnType<T>[] = [];
  let currentHeader: string | undefined = undefined;

  for (const item of data) {
    const thisHeader = getHeaderLabelFromItem(item);
    if (thisHeader !== currentHeader) {
      results.push({ label: thisHeader, isHeading: true });
      currentHeader = thisHeader;
    }
    results.push(item);
  }

  return results;
};

export const toGroupedList = (list: Model[], keys: ListKeys) => {
  const { id, label, group } = keys;

  const start: Array<{ group: string; groupItems: Array<Model> }> = [];
  const groupedList = list.reduce(
    (acc: Array<{ group: string; groupItems: Array<Model> }>, curr: Model) => {
      const existingGroup = acc.find((x) => x.group === curr[group]);
      if (!!existingGroup) {
        return [
          ...acc.filter((x) => x.group !== curr[group]),
          {
            group: curr[group],
            groupItems: [...existingGroup.groupItems, curr],
          },
        ];
      }
      return [...acc, { group: curr[group], groupItems: [curr] }];
    },
    start
  );

  const sortedGroupList = groupedList
    .map((x) => {
      return {
        group: x.group,
        groupItems: x.groupItems.sort((a, b) => (a[label] < b[label] ? -1 : 1)),
      };
    })
    .sort((a, b) => a.group.localeCompare(b.group));

  return sortedGroupList.reduce(
    (acc: Model[], curr: { group: string; groupItems: Array<Model> }) => {
      const mappedGroupItems = curr.groupItems.map((x) => {
        return {
          id: x[id],
          label: x[label],
          active: x.active
        };
      });
      return [
        ...acc,
        { isHeading: true, label: curr.group },
        ...mappedGroupItems,
      ];
    },
    []
  );
};
